/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';
import { Button, Select } from '@pickleballinc/react-ui';
import { useUser } from '@pickleballinc/ui/components/global/UserInfoProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { BenchCard } from '@/modules/league/new-components/groups-components/GroupsComponent/BenchCard';
import { GroupCard } from '@/modules/league/new-components/groups-components/GroupsComponent/GroupCard';
import {
  useSaveGroupCourts,
  useSaveGroupLineup,
  useSaveGroupSplits,
  useSaveStatsReseed,
} from '@/modules/league/new-components/groups-components/hooks';
import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  GetGameDayDetailsRes,
  GroupPlayer,
  GroupsResult,
  PlayersOnBench,
  PoolSplit,
} from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { LeagueCourts } from '@/types/leagues.types';
import { Session } from '@/types/types';

interface GroupsContentProps {
  headerContent: GetGameDayDetailsRes;
  user?: Session;
  groupsData: GroupsResult;
  ratingSessionPlayersOnBench: { ratingSessionPlayersOnBench: PlayersOnBench[] };
  isAuthorized: boolean;
}

const GroupsComponent = ({
  headerContent,
  user,
  groupsData,
  ratingSessionPlayersOnBench,
  isAuthorized,
}: GroupsContentProps) => {
  const userData = useUser();
  const { details } = headerContent;
  const { groups, poolSplits, courts, matchGroups } = groupsData;
  const queryClient = useQueryClient();
  const foundSplit = poolSplits.find(
    (split) => split.selectedPoolTemplateID === split.PoolTemplateID.toString()
  );
  const poolTempl = foundSplit ? foundSplit : poolSplits[0];
  const ratingSessionPlayersOnBenchNumber = ratingSessionPlayersOnBench?.ratingSessionPlayersOnBench
    ? ratingSessionPlayersOnBench?.ratingSessionPlayersOnBench?.length
    : 0;
  const matchDateId = details.MatchDateID;
  const buttonClasses =
    'rounded-md border 5rt3 bg-white !px-3 !py-2 !text-xs lg:!text-sm font-semibold !md:text-sm tracking-wider !text-leagues-500 hover:!text-gray-900 border-gray-300 shadow-xs';

  const [groupPlayers, setGroupPlayers] = useState<GroupPlayer[]>(groups);

  const [showPopup, setShowPopup] = useState(false);
  const [showSavePopup, setShowSavePopup] = useState(false);

  const [brakdown, setBrakdown] = useState('');
  const [poolTemplate, setPoolTemplate] = useState(0);

  const optionsCourts = courts.map((item) => ({
    value: item.MaxCourtsNeeded.toString(),
    label: `${item.MaxCourtsNeeded === 1 ? `${item.MaxCourtsNeeded} Court` : item.MaxCourtsNeeded === -1 ? 'Any Courts Count' : `${item.MaxCourtsNeeded} Courts`}`,
    originalItem: item,
  }));

  courts.push({ MaxCourtsNeeded: -1 });
  optionsCourts.sort((a, b) => {
    const valueA = parseInt(a.value, 10);
    const valueB = parseInt(b.value, 10);
    return valueA - valueB;
  });
  const cour = courts.find((court) => details.TotalCourts === court.MaxCourtsNeeded);
  const corstate = cour ? cour : courts[0];
  const [selectedPoolTemplate, setSelectedPoolTemplate] = useState<PoolSplit | undefined>(
    poolTempl
  );
  const [selectedCourt, setSelectedCourt] = useState<LeagueCourts | undefined>(corstate);
  const [selectedCourtRating, setSelectedCourtRating] = useState<number | undefined>(
    corstate?.MaxCourtsNeeded
  );
  const [selectedOptionCourt, setSelectedOptionCourt] = useState<LeagueCourts | undefined>(
    selectedCourt
  );
  const [selectedOptionSplit, setSelectedOptionSplit] = useState<string | undefined>(
    poolTempl?.Title
  );
  const [selectedOptionSplitValue, setSelectedOptionSplitValue] = useState<number | undefined>(
    poolTempl?.PoolTemplateID
  );
  const [isValid, setIsValid] = useState(false);

  const validateGroupSizes = (): boolean => {
    // Creating groups object
    // -> key is PoolID, value is array of players in that group

    const groupsNew: { [key: string]: GroupPlayer[] } = {};

    // Group Breakdown, e.g. 5,4,4
    const groupLengths: number[] = [];

    // Group players by ID
    groupPlayers.forEach((player) => {
      if (groupsNew[player.PoolID]) {
        groupsNew[player.PoolID]?.push(player);
      } else {
        groupsNew[player.PoolID] = [player];
      }
    });

    // Check length of each group
    let i = 0;

    if (selectedPoolTemplate) {
      for (let i = 0; i < selectedPoolTemplate?.PoolMaxSize_TotalCount; i++) {
        groupLengths.push(selectedPoolTemplate?.PoolMaxSize);
      }

      for (let i = 0; i < selectedPoolTemplate?.PoolMinSize_TotalCount; i++) {
        groupLengths.push(selectedPoolTemplate?.PoolMinSize);
      }
    }
    for (const [, playersgr] of Object.entries(groupsNew)) {
      const groupLength = playersgr.length;
      if (!groupLengths.includes(groupLength) && groups) {
        for (let j = 0; j < groupLengths.length; j++) {
          for (i = 0; i < poolSplits.length; i++) {
            const currentPoolSplit = poolSplits[i]; // Store the current pool split
            if (
              currentPoolSplit &&
              (currentPoolSplit.PoolMaxSize === groupLength ||
                currentPoolSplit.PoolMinSize === groupLength)
            ) {
              setPoolTemplate(currentPoolSplit.PoolTemplateID);
              return true;
            } else {
              setIsValid(false);
              continue;
            }
          }
        }
      } else {
        if (selectedPoolTemplate) {
          setPoolTemplate(selectedPoolTemplate?.PoolTemplateID);
        }
        return true;
      }
    }
    if (isValid) {
      if (selectedPoolTemplate) {
        setPoolTemplate(selectedPoolTemplate?.PoolTemplateID);
      }
      return true;
    } else {
      return false;
    }
  };
  const calculateCourts = (totalPlayers: number, isSingle: boolean): number[] => {
    // Calculate courts based on match type
    const courtsNeeded = isSingle ? Math.floor(totalPlayers / 4) : Math.floor(totalPlayers / 2);

    // Generate an array with the court numbers [1, 2, 3, ..., courtsNeeded]
    return Array.from({ length: courtsNeeded }, (_, index) => index + 1);
  };
  const movePlayerToEmptyGroup = (
    reorderedPlayers: GroupPlayer[],
    player1Idx: number,
    player2Idx: number
  ) => {
    const currentGroupIdx = matchGroups.findIndex(
      (el) => el.PoolID === reorderedPlayers[player1Idx]?.PoolID
    );

    let desiredGroupIdx: number = 0;
    let newSeedNumber: number;

    if (currentGroupIdx !== undefined && currentGroupIdx >= 0) {
      if (player2Idx < player1Idx) {
        desiredGroupIdx = currentGroupIdx - 1;
        newSeedNumber = 1;
      } else if (player2Idx > player1Idx) {
        desiredGroupIdx = currentGroupIdx + 1;
        newSeedNumber = groups.length || 0;
      }
    }

    if (desiredGroupIdx !== undefined && desiredGroupIdx >= 0) {
      const newPoolID = matchGroups[desiredGroupIdx]?.PoolID;
      if (reorderedPlayers[player1Idx]) {
        reorderedPlayers[player1Idx].OrderChanged = true;
        reorderedPlayers[player1Idx].CurrentSeedNumber = newSeedNumber!;
        reorderedPlayers[player1Idx].PoolID = newPoolID!;
      }
    }
  };

  const changeOrderInGroup = (player1Idx: number, player2Idx: number) => {
    if (groupPlayers) {
      const reorderedPlayers = [...groupPlayers];

      if (!reorderedPlayers[player2Idx]) {
        // Handling Logic for Regrouping Players if group is empty
        movePlayerToEmptyGroup(reorderedPlayers, player1Idx, player2Idx);
        return setGroupPlayers(reorderedPlayers);
      }

      if (reorderedPlayers[player1Idx]?.OrderChanged)
        reorderedPlayers[player1Idx].OrderChanged = true;
      reorderedPlayers[player2Idx].OrderChanged = true;
      if (matchGroups.length === 1 || groups[player1Idx]?.PoolID === groups[player2Idx]?.PoolID) {
        if (reorderedPlayers[player1Idx]?.PoolID !== reorderedPlayers[player2Idx].PoolID) {
          if (reorderedPlayers[player1Idx]?.PoolID)
            reorderedPlayers[player1Idx].PoolID = reorderedPlayers[player2Idx].PoolID;
        } else {
          [
            reorderedPlayers[player1Idx].PoolSeedNumber,
            reorderedPlayers[player2Idx].PoolSeedNumber,
            reorderedPlayers[player1Idx].CurrentSeedNumber,
            reorderedPlayers[player2Idx].CurrentSeedNumber,
          ] = [
            reorderedPlayers[player2Idx].PoolSeedNumber,
            reorderedPlayers[player1Idx]?.PoolSeedNumber || 0,
            reorderedPlayers[player2Idx].CurrentSeedNumber,
            reorderedPlayers[player1Idx]?.CurrentSeedNumber || 0,
          ];

          [reorderedPlayers[player1Idx], reorderedPlayers[player2Idx]] = [
            reorderedPlayers[player2Idx],
            reorderedPlayers[player1Idx],
          ];
        }
      } else {
        if (reorderedPlayers[player1Idx]?.PoolID !== reorderedPlayers[player2Idx].PoolID) {
          if (reorderedPlayers[player1Idx]?.PoolID)
            reorderedPlayers[player1Idx].PoolID = reorderedPlayers[player2Idx].PoolID;
        } else {
          [
            reorderedPlayers[player1Idx].CurrentSeedNumber,
            reorderedPlayers[player2Idx].CurrentSeedNumber,
          ] = [
            reorderedPlayers[player2Idx].CurrentSeedNumber,
            reorderedPlayers[player1Idx]?.CurrentSeedNumber,
          ];

          [reorderedPlayers[player1Idx], reorderedPlayers[player2Idx]] = [
            reorderedPlayers[player2Idx],
            reorderedPlayers[player1Idx],
          ];
        }
      }

      return setGroupPlayers(reorderedPlayers);
    }
  };

  const { mutate: saveGroupLineup } = useSaveGroupLineup();
  const { mutate: saveGroupSplits } = useSaveGroupSplits();
  const { mutate: saveGroupCourts } = useSaveGroupCourts();
  const { mutate: saveStatsReseed } = useSaveStatsReseed();
  const { setLoading } = useLoader();

  useEffect(() => {
    setLoading(false);
    setGroupPlayers(groups);
  }, [groupsData]);

  useEffect(() => {
    setSelectedPoolTemplate(poolTempl);
    setSelectedOptionSplit(poolTempl?.Title);
    setSelectedOptionSplitValue(poolTempl?.PoolTemplateID);
  }, [poolTempl, groups]);

  const optionsPoolSplits = poolSplits.map((item) => ({
    value: item.PoolTemplateID.toString(),
    label: `${item.Title}`,
    originalItem: item,
  }));

  const customCourts = calculateCourts(
    groups.length + ratingSessionPlayersOnBenchNumber,
    !details?.partnerOrSingles
  );
  const optionsCourtsCustom = customCourts.map((item) => ({
    value: item.toString(),
    label: `${item} ${item === 1 ? 'Court' : 'Courts'}`,
    originalItem: item,
  }));

  const totalPlayers = details.isRatingLeague
    ? groups.length + ratingSessionPlayersOnBenchNumber
    : groups.length;

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const searchParams = useSearchParams();
  const router = useRouter();
  const newSearchParams = new URLSearchParams(searchParams?.toString());

  useEffect(() => {
    const isMyGroupClicked = searchParams?.get('myGroup');
    const groupOfMatchingPlayer = isMyGroupClicked
      ? matchGroups.filter((group) => group.PoolID === isMyGroupClicked)
      : [];
    if (isMyGroupClicked) {
      const targetGroup =
        groupOfMatchingPlayer[0] && groupRefs.current[groupOfMatchingPlayer[0]?.PoolID];
      if (targetGroup) {
        targetGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });
        newSearchParams.delete('myGroup');
        router.replace(`?${newSearchParams.toString()}`, { scroll: false });
      }
    }
  }, [searchParams]);

  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: state.isFocused ? '#f63d68' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px #f63d68' : 'none',
      '&:hover': {
        borderColor: '#f63d68',
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#f63d68B3' : state.isFocused ? '#f63d6833' : 'white',
    }),
  };

  type SelectOption = {
    value: number;
    label: string;
  };
  type CourtsOption = {
    value: number;
    label: string;
    originalItem: LeagueCourts;
  };

  return (
    <>
      <div className='mx-auto w-full px-2 md:px-3'>
        <div className={`flex flex-col justify-between py-2 md:flex-row`}>
          {details.isMatchStarted || !isAuthorized ? (
            <>
              {details.isRatingLeague ? (
                <div className='flex w-full flex-row justify-between'>
                  <p className='text-sm text-gray-500'>
                    {details.TotalCourts !== -1
                      ? `${details.TotalCourts} ${details.TotalCourts === 1 ? 'Court' : 'Courts'}`
                      : 'Any Courts Count'}
                  </p>
                  <p className='text-sm text-gray-500'>{`${totalPlayers} Total Players`}</p>
                </div>
              ) : (
                <>
                  <div className='flex flex-row justify-between md:flex-col'>
                    {!details.isRatingLeague && (
                      <p className='mb-2 text-sm text-gray-500'>
                        {details.TotalCourts !== -1
                          ? `${details.TotalCourts} ${details.TotalCourts === 1 ? 'Court' : 'Courts'}`
                          : 'Any Courts Count'}
                      </p>
                    )}

                    {!details.isRatingLeague && (
                      <p className='mb-2 text-sm text-gray-500 md:mb-0'>{poolTempl?.Title}</p>
                    )}
                  </div>
                  <div className='flex flex-col text-right'>
                    <p className='mb-2 text-sm text-gray-500'>{`${totalPlayers} Total Players`}</p>
                    {!details.isRatingLeague && (
                      <p className='text-sm text-gray-500'>
                        {`${groups[0]?.TotalReminaingGames} out of ${groups[0]?.MatchCount} total matches remaining`}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className='flex w-full flex-col gap-2'>
              <div className='flex w-full justify-between gap-2'>
                {!details.isRatingLeague && isAuthorized ? (
                  <>
                    {courts && courts.length > 1 && details.rankingType !== 'UpDown' ? (
                      <Select
                        options={optionsCourts}
                        styles={selectStyles}
                        onChange={(newValue: unknown) => {
                          const option = newValue as {
                            value: string;
                            label: string;
                            originalItem?: { MaxCourtsNeeded: number };
                          } | null;
                          setLoading(true);
                          const selectedCourt = option?.originalItem;
                          setSelectedOptionCourt(selectedCourt);
                          setSelectedCourt(selectedCourt);
                          const totalCourts = selectedCourt ? selectedCourt.MaxCourtsNeeded : -1;
                          const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`;
                          if (fullName) {
                            saveGroupCourts(
                              { matchDateId, totalCourts, fullName },
                              {
                                onSuccess: (data) => {
                                  if (data.message) {
                                    toast.success(data.message);
                                  }
                                  queryClient.invalidateQueries(['league_game_day_header']);
                                },
                                onError: () => {
                                  toast.error('Failed to save groups court.');
                                  setLoading(false);
                                },
                              }
                            );
                          }
                        }}
                        value={
                          selectedOptionCourt
                            ? {
                                value: selectedOptionCourt.MaxCourtsNeeded.toString(),
                                label: ` ${selectedOptionCourt.MaxCourtsNeeded === -1 ? 'Any Courts Count' : selectedOptionCourt.MaxCourtsNeeded === 1 ? `${selectedOptionCourt.MaxCourtsNeeded} Court` : `${selectedOptionCourt.MaxCourtsNeeded} Courts`}`,
                              }
                            : null
                        }
                      />
                    ) : details.rankingType === 'UpDown' ? (
                      <div className='flex w-full flex-col gap-2'>
                        <div className='flex w-full flex-row justify-between gap-2'>
                          <div className='w-2/3 md:w-44'>
                            <Select
                              options={optionsCourts}
                              styles={selectStyles}
                              onChange={(newValue: unknown) => {
                                const option = newValue as {
                                  value: string;
                                  label: string;
                                  originalItem?: LeagueCourts;
                                } | null;

                                setLoading(true);

                                const selectedCourt = option?.originalItem;

                                setSelectedOptionCourt(selectedCourt);
                                setSelectedCourt(selectedCourt);

                                const totalCourts = selectedCourt
                                  ? selectedCourt.MaxCourtsNeeded
                                  : -1;
                                const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`;

                                if (fullName) {
                                  saveGroupCourts(
                                    { matchDateId, totalCourts, fullName },
                                    {
                                      onSuccess: (data) => {
                                        if (data.message) {
                                          toast.success(data.message);
                                        }
                                        queryClient.invalidateQueries(['league_game_day_header']);
                                      },
                                      onError: () => {
                                        toast.error('Failed to save groups court.');
                                        setLoading(false);
                                      },
                                    }
                                  );
                                }
                              }}
                              value={
                                selectedOptionCourt
                                  ? {
                                      value: selectedOptionCourt.MaxCourtsNeeded.toString(),
                                      label: ` ${selectedOptionCourt.MaxCourtsNeeded === -1 ? 'Any Courts Count' : selectedOptionCourt.MaxCourtsNeeded === 1 ? `${selectedOptionCourt.MaxCourtsNeeded} Court` : `${selectedOptionCourt.MaxCourtsNeeded} Courts`}`,
                                    }
                                  : null
                              }
                            />
                          </div>

                          <p className='flex items-center text-sm text-gray-500'>{`${totalPlayers} Total Players`}</p>
                        </div>
                        <div className='flex w-full flex-row justify-between gap-2'>
                          <div className='w-2/3 md:w-1/4'>
                            <Select
                              options={optionsPoolSplits}
                              styles={selectStyles}
                              onChange={(newValue: unknown) => {
                                const option = newValue as {
                                  value: string;
                                  label: string;
                                } | null;

                                if (!option) return;

                                setLoading(true);

                                const selectedPoolSplit = parseInt(option.value);
                                const selectedPoolSplitL = poolSplits.find(
                                  (split) => split.PoolTemplateID === Number(option.value)
                                );

                                setSelectedOptionSplit(option.label);
                                setSelectedOptionSplitValue(Number(option.value));
                                setSelectedPoolTemplate(selectedPoolSplitL);

                                const poolTemplateId =
                                  brakdown === '' ? selectedPoolSplit : poolTemplate;
                                setLoading(true);
                                if (poolTemplateId) {
                                  saveGroupSplits(
                                    { matchDateId, brakdown, poolTemplateId },
                                    {
                                      onSuccess: (data) => {
                                        if (data.message) {
                                          toast.success(data.message);
                                        }
                                        queryClient.invalidateQueries(['league_game_day_header']);
                                        queryClient.invalidateQueries(['league_groups']);
                                      },
                                      onError: () => {
                                        toast.error('Failed to save group splits.');
                                        setLoading(false);
                                      },
                                    }
                                  );
                                }
                              }}
                              value={
                                selectedOptionSplit
                                  ? {
                                      value: selectedOptionSplitValue?.toString(),
                                      label: `${selectedOptionSplit}`,
                                    }
                                  : null
                              }
                            />
                          </div>
                          <p className='flex items-center text-sm text-gray-500'>
                            {`${details.MatchCount}
															Total Matches`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className='flex items-center text-sm text-gray-500'>
                        {details.TotalCourts !== -1
                          ? `${details.TotalCourts} ${details.TotalCourts === 1 ? 'Court' : 'Courts'}`
                          : 'Any Courts Count'}
                      </p>
                    )}
                    {poolSplits && poolSplits.length > 1 && details.rankingType !== 'UpDown' ? (
                      <div className='w-2/3 md:w-1/4'>
                        <Select
                          options={optionsPoolSplits}
                          styles={selectStyles}
                          onChange={(newValue: unknown) => {
                            const option = newValue as SelectOption | null;
                            if (!option) return;

                            setLoading(true);

                            const selectedPoolSplit = Number(option.value);

                            const selectedPoolSplitL = poolSplits.find(
                              (split) => split.PoolTemplateID === selectedPoolSplit
                            );

                            setSelectedOptionSplit(option.label);
                            setSelectedOptionSplitValue(selectedPoolSplit);
                            setSelectedPoolTemplate(selectedPoolSplitL);

                            const poolTemplateId =
                              brakdown === '' ? selectedPoolSplit : poolTemplate;
                            setLoading(true);
                            if (poolTemplateId)
                              saveGroupSplits(
                                { matchDateId, brakdown, poolTemplateId },
                                {
                                  onSuccess: (data) => {
                                    if (data.message) {
                                      toast.success(data.message);
                                    }
                                    queryClient.invalidateQueries(['league_game_day_header']);
                                  },
                                  onError: () => {
                                    toast.error('Failed to save group splits.');
                                    setLoading(false);
                                  },
                                }
                              );
                          }}
                          value={
                            selectedOptionSplit
                              ? {
                                  value: selectedOptionSplitValue?.toString(),
                                  label: `${selectedOptionSplit}`,
                                }
                              : null
                          }
                        />
                      </div>
                    ) : details.rankingType !== 'UpDown' ? (
                      <p className='flex items-center text-sm text-gray-500'>{poolTempl?.Title}</p>
                    ) : null}
                  </>
                ) : (
                  <>
                    {groups ? (
                      <>
                        <Select
                          options={optionsCourtsCustom}
                          styles={selectStyles}
                          onChange={(newValue: unknown) => {
                            const option = newValue as CourtsOption | null;
                            if (!option) return;

                            const selectedCourt = option.originalItem;
                            setSelectedOptionCourt(selectedCourt);
                            setSelectedCourtRating(selectedCourt.MaxCourtsNeeded);

                            const courtCount = selectedCourt.MaxCourtsNeeded;
                            const poolTemplateId =
                              brakdown === '' ? selectedPoolTemplate?.PoolTemplateID : poolTemplate;

                            const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`;

                            const breakdownArray = details.partnerOrSingles
                              ? new Array(courtCount).fill(4)
                              : new Array(courtCount).fill(2);

                            const calculatedBreakdown = breakdownArray.join(',');

                            setLoading(true);

                            if (fullName) {
                              saveGroupCourts(
                                {
                                  matchDateId,
                                  totalCourts: selectedCourt?.MaxCourtsNeeded ?? -1,
                                  fullName,
                                },
                                {
                                  onSuccess: () => {
                                    if (poolTemplateId) {
                                      saveGroupSplits(
                                        {
                                          matchDateId,
                                          brakdown: calculatedBreakdown,
                                          poolTemplateId,
                                        },
                                        {
                                          onSuccess: (data) => {
                                            if (data.message) {
                                              toast.success(data.message);
                                            }
                                            queryClient.invalidateQueries([
                                              'league_game_day_header',
                                            ]);
                                          },
                                          onError: () => {
                                            toast.error('Failed to save group splits.');
                                            setLoading(false);
                                          },
                                        }
                                      );
                                    }
                                  },
                                  onError: () => {
                                    toast.error('Failed to save group court.');
                                    setLoading(false);
                                  },
                                }
                              );
                            }
                          }}
                          value={
                            selectedCourtRating
                              ? {
                                  value: selectedCourtRating,
                                  label: `${selectedCourtRating} ${selectedCourtRating === 1 ? 'Court' : 'Courts'}`,
                                  originalItem: selectedCourtRating,
                                }
                              : null
                          }
                        />
                        <div className='flex flex-col items-center justify-center text-left text-sm text-gray-500 md:text-right'>
                          {`${totalPlayers} Total Players`}
                        </div>
                      </>
                    ) : null}
                  </>
                )}
              </div>
              {isAuthorized ? (
                <div className='flex flex-row justify-start'>
                  <Button
                    variant='link-gray'
                    className={buttonClasses}
                    onClick={() => {
                      if (matchGroups.length >= 1) {
                        if (!validateGroupSizes()) {
                          setShowPopup(true);
                        } else setShowSavePopup(true);
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant='link-gray'
                    className={`${buttonClasses} ml-4`}
                    onClick={() => {
                      if (poolSplits && poolSplits[0]) {
                        setSelectedPoolTemplate(poolSplits[0]);
                      }
                      setLoading(true);
                      saveStatsReseed(
                        { matchDateId },
                        {
                          onSuccess: (data) => {
                            if (data.message) {
                              toast.success(data.message);
                            }
                            queryClient.invalidateQueries(['league_groups']);
                          },
                          onError: () => {
                            toast.error('Failed to save reseed players by stats.');
                            setLoading(false);
                          },
                        }
                      );
                    }}
                  >
                    Seed by standing
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
        {matchGroups && groupPlayers && groupPlayers.length && groups
          ? matchGroups.map((group, i) => {
              return (
                <Fragment key={group.PoolID}>
                  {group.NumberOfTeamsInPool !== 0 && (
                    <div
                      key={group.PoolID}
                      ref={(el) => {
                        groupRefs.current[group.PoolID] = el;
                      }}
                    >
                      <GroupCard
                        players={
                          groupPlayers
                            ? groupPlayers.filter((player) => player.PoolID === group.PoolID)
                            : undefined
                        }
                        remainingGames={group.TotalReminaingGames}
                        groupNumber={i + 1}
                        groupSize={group.NumberOfTeamsInPool}
                        groupName={groups.find((g) => g.PoolID === group.PoolID)?.Shorthand}
                        gameDaySession={details}
                        gameDayInfo={details}
                        isFirstGroup={i === 0}
                        isLastGroup={i === matchGroups.length - 1}
                        changeOrderInGroup={changeOrderInGroup}
                        matchDateId={matchDateId}
                        matchesStarted={!!details.isMatchStarted}
                        playerType={details.playerTypeTitle}
                        matchGroups={matchGroups}
                        isFlexLeague={details.isFlexLeague}
                        isAuthorized={isAuthorized}
                        userUuid={user?.pbUuid}
                        isRatingLeague={details.isRatingLeague}
                        isBench={ratingSessionPlayersOnBenchNumber !== 0 && details.isRatingLeague}
                        poolId={group.PoolID}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })
          : null}
        {ratingSessionPlayersOnBenchNumber !== 0 && details.isRatingLeague ? (
          <>
            {details.isRatingLeague && ratingSessionPlayersOnBench ? (
              <BenchCard
                players={ratingSessionPlayersOnBench?.ratingSessionPlayersOnBench}
                groupSize={ratingSessionPlayersOnBenchNumber}
                gameDayInfo={details}
                changeOrderInGroup={changeOrderInGroup}
                matchDateId={matchDateId}
                matchesStarted={!!details.isMatchStarted}
                matchesCompleted={details.completedAndLocked ? true : false}
                playerType={details.playerTypeTitle}
                matchGroups={matchGroups}
                isAuthorized={isAuthorized}
                userUuid={user?.pbUuid}
              />
            ) : null}
          </>
        ) : null}
      </div>
      <ConfirmationModal
        open={showSavePopup}
        onClose={() => setShowSavePopup(false)}
        onConfirm={() => {
          const breakdownArr: number[] = [];
          matchGroups.forEach((matchGroup) => {
            breakdownArr.push(
              groupPlayers.filter((player) => player.PoolID === matchGroup.PoolID).length
            );
          });
          const countBreakdown: string = [breakdownArr].join(',') + ',';
          const modifiedPlayers = groupPlayers.map(({ SessionTeamID, CurrentSeedNumber }) => ({
            SessionTeamID,
            CurrentSeedNumber,
          }));
          modifiedPlayers.forEach((player, i) => {
            player.CurrentSeedNumber = i + 1;
          });
          setBrakdown(countBreakdown);
          setLoading(true);
          saveGroupLineup(
            { matchDateId, modifiedPlayers, countBreakdown },
            {
              onSuccess: (data) => {
                const poolTemplateId =
                  brakdown === '' ? selectedPoolTemplate?.PoolTemplateID : poolTemplate;

                if (poolTemplateId) {
                  saveGroupSplits(
                    { matchDateId, brakdown, poolTemplateId },
                    {
                      onSuccess: () => {
                        if (data.message) {
                          toast.success(data.message);
                        }
                        queryClient.invalidateQueries(['league_game_day_header']);
                        queryClient.invalidateQueries(['league_groups']);
                      },
                      onError: () => {
                        toast.error('Failed to save group splits.');
                        setLoading(false);
                      },
                    }
                  );
                }
              },
              onError: () => {
                toast.error('Failed to save group lineup.');
                setLoading(false);
              },
            }
          );
          setShowSavePopup(false);
        }}
        title='Save Group Lineup'
        message='This will save the current group lineup.'
        confirmButtonText='Save'
        doNotShowIcon
      />
      <ConfirmationModal
        open={showPopup}
        onClose={() => setShowPopup(false)}
        onConfirm={() => {
          setShowPopup(false);
        }}
        title='Invalid Groups'
        message='One or more groups do NOT have the correct number of players.'
        confirmButtonText='Yes'
        iconColor='error-500'
      />
    </>
  );
};

export default GroupsComponent;
